<template>
  <section :class="modalOnly ? 'cert-card-modal-only' : 'vp-card'" data-profile-card="certifications">
    <div
      v-if="!modalOnly"
      class="vp-card-head"
      :class="{ 'pv-accordion-head': collapsible }"
      :role="collapsible ? 'button' : null"
      :tabindex="collapsible ? 0 : null"
      :aria-expanded="collapsible ? isExpanded : null"
      @click="toggleCard"
      @keydown.enter.self.prevent="toggleCard"
      @keydown.space.self.prevent="toggleCard"
    >
      <h6 class="vp-card-title">
        <i class="ph-duotone ph-certificate"></i>
        {{ $t('profile.sections.certifications') }}
        <span v-if="certificates.length" class="vp-chip">{{ $formatInteger(certificates.length) }}</span>
      </h6>
      <div class="d-flex align-items-center gap-2">
        <button type="button" class="vp-btn vp-btn-ghost vp-btn-sm" @click.stop="openManageModal">
          <i class="ph-duotone ph-list-bullets"></i>
          {{ $t('profile.certifications.viewAllManage') }}
        </button>
        <i v-if="collapsible" class="ph-duotone pv-accordion-caret" :class="isExpanded ? 'ph-caret-up' : 'ph-caret-down'"></i>
      </div>
    </div>
    <div v-show="isExpanded && !modalOnly" class="vp-card-body">
      <!-- Upload progress -->
      <div v-if="isUploading" class="progress cert-progress mb-3">
        <div class="progress-bar progress-bar-striped progress-bar-animated"
             role="progressbar"
             :style="{ width: uploadProgress + '%' }"
             :aria-valuenow="uploadProgress"
             aria-valuemin="0"
             aria-valuemax="100">
          {{ uploadProgress }}%
        </div>
      </div>

      <!-- Error message -->
      <div v-if="error" class="vp-note vp-note--danger mb-3" role="alert">
        {{ error }}
      </div>

      <div v-if="isLoading" class="vp-loading">
        <span class="spinner-border spinner-border-sm text-primary" role="status">
          <span class="visually-hidden">{{ $t('common.loading') }}</span>
        </span>
      </div>
      <div v-else-if="!certificates.length" class="vp-empty">
        <i class="ph-duotone ph-certificate vp-empty-icon"></i>
        <div class="vp-empty-text">{{ $t('profile.certifications.noCertificates') }}</div>
      </div>
      <div v-else class="compact-list">
        <div
          v-for="cert in compactCertificates"
          :key="cert.id"
          class="compact-item"
        >
          <div class="vp-min-w-0">
            <div class="compact-item-name vp-truncate">{{ cert.name }}</div>
            <div class="compact-item-dates">{{ formatDate(cert.issueDate) }} - {{ formatDate(cert.expirationDate) }}</div>
          </div>
          <span v-if="cert.isExpired" class="vp-chip vp-chip--danger">
            {{ $t('profile.certifications.expired') }}
          </span>
          <span v-else-if="cert.daysUntilExpiration <= 30" class="vp-chip vp-chip--warning">
            {{ $t('profile.certifications.expiringSoon', { days: cert.daysUntilExpiration }) }}
          </span>
          <span v-else class="vp-chip vp-chip--success">
            {{ $t('profile.certifications.valid') }}
          </span>
        </div>
      </div>
      <!-- Hidden file input -->
      <input
        type="file"
        ref="fileInput"
        class="d-none"
        accept="application/pdf"
        @change="handleFileUpload"
        multiple
      />

    </div>

    <!--
      The dialogs live outside the collapsible body on purpose: inside it a collapsed
      card — or modal-only mode — would hide them with display:none, and Bootstrap would
      still draw its backdrop, dimming the screen over a dialog nobody can see.
    -->
    <!-- PDF Preview Modal - Updated to match RfqAttachmentsCard approach -->
    <div class="modal fade" id="pdfPreviewModal" tabindex="-1" aria-labelledby="pdfPreviewModalLabel" aria-hidden="true">
      <div class="modal-dialog modal-xl">
        <div class="modal-content vp-modal">
          <div class="vp-modal-head">
            <h5 class="vp-modal-title vp-min-w-0" id="pdfPreviewModalLabel">
              <i class="ph-duotone ph-file-pdf"></i>
              <span class="vp-truncate">{{ getPreviewFileName(previewingFile) }}</span>
            </h5>
            <button type="button" class="vp-modal-close" data-bs-dismiss="modal" :aria-label="$t('common.close')">
              <i class="ph-duotone ph-x"></i>
            </button>
          </div>
          <div class="vp-modal-body cert-preview-body">
            <div v-if="isLoadingPreview" class="vp-loading">
              <span class="spinner-border spinner-border-sm text-primary" role="status">
                <span class="visually-hidden">{{ $t('common.loading') }}</span>
              </span>
            </div>
            <div v-else-if="previewError" class="vp-note vp-note--danger">
              {{ previewError }}
            </div>
            <div v-else-if="previewFileData" class="preview-container">
              <vue-files-preview
                :file="previewFileData"
                :excelOptions="{
                  mode: 'edit',
                  showToolbar: true,
                  showGrid: true,
                  showContextmenu: false
                }"
                @success="handlePreviewSuccess"
                @error="handlePreviewError"
              />
            </div>
          </div>
          <div class="vp-modal-foot">
            <button type="button" class="vp-btn vp-btn-ghost" data-bs-dismiss="modal">{{ $t('common.close') }}</button>
            <button type="button" class="vp-btn vp-btn-primary" @click="downloadCurrentPreview">
              <i class="ph-duotone ph-download-simple"></i> {{ $t('common.download') }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Add Certificate Modal -->
    <div class="modal fade" id="addCertificateModal" tabindex="-1" aria-labelledby="addCertificateModalLabel" aria-hidden="true">
      <div class="modal-dialog">
        <div class="modal-content vp-modal">
          <div class="vp-modal-head">
            <h5 class="vp-modal-title" id="addCertificateModalLabel">
              <i class="ph-duotone ph-certificate"></i>{{ $t('profile.certifications.addCertificate') }}
            </h5>
            <button type="button" class="vp-modal-close" data-bs-dismiss="modal" :aria-label="$t('common.close')">
              <i class="ph-duotone ph-x"></i>
            </button>
          </div>
          <div class="vp-modal-body">
            <form class="cert-form" @submit.prevent="submitCertificate">
              <!-- Certificate Selection -->
              <div class="cert-field">
                <label class="vp-label" for="cert-type">
                  {{ $t('profile.certifications.certificateType') }}
                  <span class="vp-label-req">*</span>
                </label>
                <select
                  id="cert-type"
                  class="vp-control"
                  v-model="certificateForm.certificateId"
                  :class="{ 'is-invalid': certificateFormErrors.certificateId }"
                  @change="onCertificateChange"
                  required
                >
                  <option value="">{{ $t('common.pleaseSelect') }}</option>
                  <option
                    v-for="cert in availableCertificates"
                    :key="cert.id"
                    :value="cert.id"
                  >
                    {{ cert.name }} {{ cert.code ? `(${cert.code})` : '' }}
                  </option>
                </select>
                <div v-if="certificateFormErrors.certificateId" class="vp-help vp-help--error">
                  {{ certificateFormErrors.certificateId }}
                </div>
              </div>

              <!-- Certificate Number (Optional) -->
              <div class="cert-field">
                <label class="vp-label" for="cert-number">{{ $t('profile.certifications.certificateNumber') }}</label>
                <input
                  id="cert-number"
                  type="text"
                  class="vp-control"
                  v-model="certificateForm.certificateNumber"
                  :placeholder="$t('profile.certifications.certificateNumberPlaceholder')"
                />
              </div>

              <!-- Issue Date -->
              <div class="cert-field">
                <label class="vp-label" for="cert-issue-date">
                  {{ $t('profile.certifications.issueDate') }}
                  <span class="vp-label-req">*</span>
                </label>
                <div class="vp-input-group cert-date-group">
                  <input
                    id="cert-issue-date"
                    type="text"
                    class="vp-control"
                    v-model="formattedIssueDate"
                    :class="{ 'is-invalid': certificateFormErrors.issueDate }"
                    :placeholder="dateFormat"
                    @input="onFormattedIssueDateChange"
                    required
                  />
                  <input
                    type="date"
                    class="cert-date-picker"
                    v-model="certificateForm.issueDate"
                    @change="onIssueDateChange"
                    :title="$t('profile.certifications.openDatePicker')"
                    :aria-label="$t('profile.certifications.openDatePicker')"
                  />
                  <span class="vp-input-addon">
                    <i class="ph-duotone ph-calendar-blank"></i>
                  </span>
                </div>
                <div v-if="certificateFormErrors.issueDate" class="vp-help vp-help--error">
                  {{ certificateFormErrors.issueDate }}
                </div>
                <div class="vp-help">{{ $t('profile.certifications.dateFormatHelp', { format: dateFormat }) }}</div>
              </div>

              <!-- Expire Date -->
              <div class="cert-field">
                <label class="vp-label" for="cert-expire-date">
                  {{ $t('profile.certifications.expireDate') }}
                  <span class="vp-label-req">*</span>
                </label>
                <div class="vp-input-group cert-date-group">
                  <input
                    id="cert-expire-date"
                    type="text"
                    class="vp-control"
                    v-model="formattedExpireDate"
                    :class="{ 'is-invalid': certificateFormErrors.expireDate }"
                    :placeholder="dateFormat"
                    @input="onFormattedExpireDateChange"
                    required
                  />
                  <input
                    type="date"
                    class="cert-date-picker"
                    v-model="certificateForm.expireDate"
                    :title="$t('profile.certifications.openDatePicker')"
                    :aria-label="$t('profile.certifications.openDatePicker')"
                  />
                  <span class="vp-input-addon">
                    <i class="ph-duotone ph-calendar-blank"></i>
                  </span>
                </div>
                <div v-if="certificateFormErrors.expireDate" class="vp-help vp-help--error">
                  {{ certificateFormErrors.expireDate }}
                </div>
                <div class="vp-help">
                  <span v-if="selectedCertificateInterval">
                    {{ $t('profile.certifications.autoCalculated', { interval: selectedCertificateInterval }) }}
                  </span>
                  <span v-else>
                    {{ $t('profile.certifications.dateFormatHelp', { format: dateFormat }) }}
                  </span>
                </div>
              </div>

              <!-- Certificate File Upload (Required) -->
              <div class="cert-field">
                <label class="vp-label" for="cert-file">
                  {{ $t('profile.certifications.certificateFile') }}
                  <span class="vp-label-req">*</span>
                </label>
                <input
                  id="cert-file"
                  type="file"
                  class="vp-control"
                  ref="certificateFileInput"
                  accept="application/pdf,.pdf"
                  @change="onCertificateFileChange"
                  :class="{ 'is-invalid': certificateFormErrors.file }"
                  required
                />
                <div v-if="certificateFormErrors.file" class="vp-help vp-help--error">
                  {{ certificateFormErrors.file }}
                </div>
                <div class="vp-help">{{ $t('profile.certifications.fileHelp') }}</div>

                <!-- Upload Progress -->
                <div v-if="isCertificateFileUploading" class="progress cert-progress mt-2">
                  <div
                    class="progress-bar progress-bar-striped progress-bar-animated"
                    role="progressbar"
                    :style="{ width: certificateFileUploadProgress + '%' }"
                  >
                    {{ certificateFileUploadProgress }}%
                  </div>
                </div>

                <!-- Selected File Info -->
                <div v-if="certificateForm.selectedFile" class="vp-note vp-note--info mt-2">
                  <i class="ph-duotone ph-file-pdf"></i>
                  <span>{{ $t('profile.certifications.fileSelected') }}: {{ certificateForm.uploadedFileName }}</span>
                </div>
              </div>
            </form>
          </div>
          <div class="vp-modal-foot">
            <button type="button" class="vp-btn vp-btn-ghost" @click="cancelCertificateModal">
              {{ $t('common.cancel') }}
            </button>
            <button
              type="button"
              class="vp-btn vp-btn-primary"
              @click="submitCertificate"
              :disabled="isSubmittingCertificate"
            >
              <span v-if="isSubmittingCertificate" class="spinner-border spinner-border-sm"></span>
              {{ $t('common.save') }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- View all / Manage Modal -->
    <div class="modal fade" id="certificatesManageModal" tabindex="-1" aria-hidden="true">
      <div class="modal-dialog modal-xl">
        <div class="modal-content vp-modal">
          <div class="vp-modal-head">
            <h5 class="vp-modal-title">
              <i class="ph-duotone ph-certificate"></i>{{ $t('profile.sections.certifications') }}
            </h5>
            <button
              v-if="canCreateCertificates"
              type="button"
              class="vp-btn vp-btn-primary vp-btn-sm ms-auto"
              @click="openAddCertificateModal(true)"
            >
              <i class="ph-duotone ph-plus"></i>
              {{ $t('profile.certifications.addCertificate') }}
            </button>
            <button
              type="button"
              class="vp-modal-close"
              :class="{ 'ms-0': canCreateCertificates }"
              data-bs-dismiss="modal"
              :aria-label="$t('common.close')"
            >
              <i class="ph-duotone ph-x"></i>
            </button>
          </div>
          <div class="vp-modal-body">
            <div v-if="!certificates.length" class="vp-empty">
              <i class="ph-duotone ph-certificate vp-empty-icon"></i>
              <div class="vp-empty-text">{{ $t('profile.certifications.noCertificates') }}</div>
            </div>
            <div v-else class="cert-grid">
              <article v-for="cert in certificates" :key="cert.id" class="cert-tile">
                <div class="cert-tile-head">
                  <div class="vp-min-w-0">
                    <div class="cert-tile-name vp-truncate" :title="cert.name">{{ cert.name }}</div>
                    <div class="cert-tile-type">{{ cert.certificateTypeName }}</div>
                  </div>
                  <button
                    v-if="canDeleteCertificates"
                    type="button"
                    class="vp-icon-btn vp-icon-btn--danger"
                    @click="handleDelete(cert)"
                    :disabled="isLoading || deletingId === cert.id"
                    :title="$t('common.delete')"
                    :aria-label="$t('common.delete')"
                  >
                    <span v-if="deletingId === cert.id" class="spinner-border spinner-border-sm"></span>
                    <i v-else class="ph-duotone ph-trash"></i>
                  </button>
                </div>
                <div class="cert-tile-dates">
                  <i class="ph-duotone ph-calendar-blank"></i>
                  {{ formatDate(cert.issueDate) }} - {{ formatDate(cert.expirationDate) }}
                </div>
                <div>
                  <span v-if="cert.isExpired" class="vp-chip vp-chip--danger">
                    {{ $t('profile.certifications.expired') }}
                  </span>
                  <span v-else-if="cert.daysUntilExpiration <= 30" class="vp-chip vp-chip--warning">
                    {{ $t('profile.certifications.expiringSoon', { days: cert.daysUntilExpiration }) }}
                  </span>
                  <span v-else class="vp-chip vp-chip--success">
                    {{ $t('profile.certifications.valid') }}
                  </span>
                </div>
                <button
                  v-if="cert.fileUploadId"
                  type="button"
                  class="vp-btn vp-btn-ghost vp-btn-sm cert-tile-view"
                  @click="handlePreviewFile(cert, true)"
                >
                  <i class="ph-duotone ph-eye"></i>
                  {{ $t('common.view') }}
                </button>
              </article>
            </div>
          </div>
          <div class="vp-modal-foot">
            <button type="button" class="vp-btn vp-btn-ghost" data-bs-dismiss="modal">
              {{ $t('common.close') }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import { ref, onMounted, onBeforeUnmount, computed } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import fileManagementService from '@/services/fileManagementService';
import { VueFilesPreview } from 'vue-files-preview';
import Swal from 'sweetalert2';
import tenantInfoService from '@/services/tenantInfoService';
import certificateService from '@/services/certificateService';
import tenantCertificateService from '@/services/tenantCertificateService';
import { useDateFormatter } from '@/composables/useDateFormatter';
import { usePermissions } from '@/composables/usePermissions';

export default {
  name: 'ProfileCertificationsCard',
  components: {
    VueFilesPreview
  },
  props: {
    /**
     * Renders only the add/edit and preview modals. Used by the Certificates screen, which
     * draws the list itself and reaches in here purely to open them.
     */
    modalOnly: {
      type: Boolean,
      default: false
    },
    isReadOnly: {
      type: Boolean,
      default: false
    },
    targetTenantId: {
      type: String,
      default: ''
    },
    /**
     * Start open. The profile page stacks these cards as an accordion where collapsed is
     * right; on the dedicated Certificates page the card is the whole screen, so opening
     * it closed would hide everything behind a click.
     */
    defaultExpanded: {
      type: Boolean,
      default: false
    },
    /**
     * False when the card is the only thing on its tab: no toggle, always open, and
     * deaf to the accordion events of cards on other tabs.
     */
    collapsible: {
      type: Boolean,
      default: true
    }
  },
  emits: ['certificates-updated'],
  setup(props, { emit }) {
    const store = useStore();
    const { t } = useI18n();
    const { formatDate } = useDateFormatter();
    const { hasPermission } = usePermissions();
    const ACCORDION_EVENT = 'profile-left-card-open';
    const cardKey = 'certifications';
    const isExpanded = ref(props.defaultExpanded || !props.collapsible);
    const certificates = ref([]);
    const isLoading = ref(false);
    const isUploading = ref(false);
    const uploadProgress = ref(0);
    const error = ref(null);
    const fileInput = ref(null);
    const totalSize = ref(0);
    const currentUser = computed(() => store.getters['auth/currentUser']);
    const deletingId = ref(null);
    const previewingFile = ref({});
    const previewFile = ref(null);
    const previewError = ref(null);
    const isLoadingPreview = ref(false);
    let pdfPreviewModal = null;
    let addCertificateModal = null;
    let certificatesManageModal = null;
    const reopenManageAfterAdd = ref(false);
    const reopenManageAfterPreview = ref(false);
    const onOtherCardOpened = (event) => {
      if (!props.collapsible) return;
      if (event?.detail?.card !== cardKey) {
        isExpanded.value = false;
      }
    };

    const toggleCard = () => {
      if (!props.collapsible) return;
      if (isExpanded.value) {
        isExpanded.value = false;
        return;
      }
      window.dispatchEvent(new CustomEvent(ACCORDION_EVENT, { detail: { card: cardKey } }));
      isExpanded.value = true;
    };


    const compactCertificates = computed(() => certificates.value.slice(0, 5));

    // Certificate form variables
    const availableCertificates = ref([]);
    const isSubmittingCertificate = ref(false);
    const selectedCertificateInterval = ref(null);
    const certificateForm = ref({
      certificateId: '',
      issueDate: new Date().toISOString().split('T')[0], // Today's date
      expireDate: '',
      certificateNumber: '',
      uploadedFileId: null,
      uploadedFileName: ''
    });
    const certificateFormErrors = ref({
      certificateId: '',
      issueDate: '',
      expireDate: '',
      file: ''
    });
    
    // File upload related variables
    const certificateFileInput = ref(null);
    const isCertificateFileUploading = ref(false);
    const certificateFileUploadProgress = ref(0);

    // Date format helpers
    const dateFormat = computed(() => store.getters['settings/dateFormat']);
    
    // Computed properties for formatted date inputs
    const formattedIssueDate = computed({
      get: () => {
        if (!certificateForm.value.issueDate) return '';
        return formatDate(certificateForm.value.issueDate);
      },
      set: (value) => {
        if (!value) {
          certificateForm.value.issueDate = '';
          return;
        }
        // Parse the formatted date back to YYYY-MM-DD format
        const parsedDate = parseFormattedDate(value);
        if (parsedDate) {
          certificateForm.value.issueDate = parsedDate;
        }
      }
    });

    const formattedExpireDate = computed({
      get: () => {
        if (!certificateForm.value.expireDate) return '';
        return formatDate(certificateForm.value.expireDate);
      },
      set: (value) => {
        if (!value) {
          certificateForm.value.expireDate = '';
          return;
        }
        // Parse the formatted date back to YYYY-MM-DD format
        const parsedDate = parseFormattedDate(value);
        if (parsedDate) {
          certificateForm.value.expireDate = parsedDate;
        }
      }
    });

    // Helper function to parse formatted date back to YYYY-MM-DD
    const parseFormattedDate = (formattedDate) => {
      if (!formattedDate) return null;
      
      try {
        // Handle different date formats
        const format = dateFormat.value;
        let day, month, year;
        
        if (format === 'DD/MM/YYYY') {
          const parts = formattedDate.split('/');
          if (parts.length === 3) {
            day = parts[0].padStart(2, '0');
            month = parts[1].padStart(2, '0');
            year = parts[2];
          }
        } else if (format === 'MM/DD/YYYY') {
          const parts = formattedDate.split('/');
          if (parts.length === 3) {
            month = parts[0].padStart(2, '0');
            day = parts[1].padStart(2, '0');
            year = parts[2];
          }
        } else if (format === 'YYYY-MM-DD') {
          const parts = formattedDate.split('-');
          if (parts.length === 3) {
            year = parts[0];
            month = parts[1].padStart(2, '0');
            day = parts[2].padStart(2, '0');
          }
        }
        
        if (day && month && year) {
          // Validate the date
          const date = new Date(year, month - 1, day);
          if (date.getFullYear() == year && date.getMonth() == month - 1 && date.getDate() == day) {
            return `${year}-${month}-${day}`;
          }
        }
      } catch (error) {
        console.error('Error parsing date:', error);
      }
      
      return null;
    };

    // Permission checks
    const canCreateCertificates = computed(() => hasPermission('Supplier.TenantCertificates.Create') && !props.isReadOnly);
    const canDeleteCertificates = computed(() => hasPermission('Supplier.TenantCertificates.Delete') && !props.isReadOnly);

    // Format file size
    const formatFileSize = (bytes) => {
      if (!bytes) return '0 B';
      
      const units = ['B', 'KB', 'MB', 'GB'];
      let size = bytes;
      let unitIndex = 0;
      
      while (size >= 1024 && unitIndex < units.length - 1) {
        size /= 1024;
        unitIndex++;
      }
      
      // Round to 2 decimal places if MB or GB, 0 decimal places if B or KB
      const decimals = unitIndex >= 2 ? 2 : 0;
      return `${size.toFixed(decimals)} ${units[unitIndex]}`;
    };

    // Update entity counts in store and emit event
    const updateCertificatesCount = async () => {
      try {
        // Get updated counts from API
        const response = await tenantInfoService.getEntityCounts(props.targetTenantId || null);
        if (response) {
          // Update store with new counts 
          store.commit('updateEntityCounts', response);
          
          // Emit event to notify parent components
          emit('certificates-updated', {
            count: certificates.value.length,
            totalSize: totalSize.value
          });
        }
      } catch (error) {
        console.error('Error updating certificate counts:', error);
      }
    };

    const loadCertificates = async () => {
      if (!currentUser.value?.tenantId) {
        console.warn('No tenantId found in user data');
        return;
      }
      
      isLoading.value = true;
      error.value = null;
      
      try {
        const response = await tenantCertificateService.getTenantCertificates(props.targetTenantId || null);
        
        if (response && Array.isArray(response)) {
          certificates.value = response.map(cert => ({
            id: cert.id,
            name: cert.certificateName,
            certificateTypeName: cert.certificateTypeName,
            certificateTypeId: cert.certificateTypeId,
            issueDate: cert.issueDate,
            expirationDate: cert.expirationDate,
            isExpired: cert.isExpired,
            daysUntilExpiration: cert.daysUntilExpiration,
            fileUploadId: cert.fileUploadId,
            fileUploadName: cert.fileUploadName,
            createdAt: cert.creationTime
          }));
        } else if (response && response.items) {
          // Handle paginated response
          certificates.value = response.items.map(cert => ({
            id: cert.id,
            name: cert.certificateName,
            certificateTypeName: cert.certificateTypeName,
            certificateTypeId: cert.certificateTypeId,
            issueDate: cert.issueDate,
            expirationDate: cert.expirationDate,
            isExpired: cert.isExpired,
            daysUntilExpiration: cert.daysUntilExpiration,
            fileUploadId: cert.fileUploadId,
            fileUploadName: cert.fileUploadName,
            createdAt: cert.creationTime
          }));
        } else {
          certificates.value = [];
        }
        
        // Calculate total size (not applicable for certificates, but kept for compatibility)
        totalSize.value = certificates.value.length;
        
        // Update the entity counts
        await updateCertificatesCount();
        
      } catch (error) {
        console.error('Error loading certificates:', error);
        error.value = t('profile.certifications.errors.loadFailed');
        store.dispatch('notify', {
          message: t('profile.certifications.errors.loadFailed'),
          type: 'error'
        });
      } finally {
        isLoading.value = false;
      }
    };

    const downloadFile = async (cert) => {
      if (!cert || !cert.fileUploadId) {
        console.error('Invalid certificate object or missing fileUploadId');
        return;
      }
      
      try {
        isLoading.value = true;
        error.value = null;
        
        // Ensure file name has .pdf extension since we only allow PDF uploads
        const fileName = cert.fileUploadName || cert.name;
        const fileNameWithExtension = fileName.endsWith('.pdf') ? fileName : `${fileName}.pdf`;
        
        const result = await fileManagementService.downloadFile(cert.fileUploadId, fileNameWithExtension);
        
        if (result && result.success) {
          store.dispatch('notify', {
            message: t('common.files.downloading', { fileName: result.filename || fileNameWithExtension }),
            type: 'success'
          });
        }
      } catch (err) {
        console.error('Error downloading file:', err);
        error.value = t('common.files.downloadFailed');
        store.dispatch('notify', {
          message: t('common.files.downloadFailed'),
          type: 'error'
        });
      } finally {
        isLoading.value = false;
      }
    };
    
    const showPreviewModal = (fromManage) => {
      if (fromManage && certificatesManageModal) {
        reopenManageAfterPreview.value = true;
        certificatesManageModal.hide();
        setTimeout(() => { pdfPreviewModal?.show(); }, 220);
      } else {
        pdfPreviewModal?.show();
      }
    };

    const buildPreviewFile = (blob, cert) => {
      const fileName = cert.fileUploadName || cert.name;
      const fileNameWithExtension = fileName.endsWith('.pdf') ? fileName : `${fileName}.pdf`;
      return new File([blob], fileNameWithExtension, { type: blob.type || 'application/pdf' });
    };

    const handlePreviewFile = async (cert, fromManage = false) => {
      if (!cert || !cert.fileUploadId) {
        console.error('Invalid certificate object or missing fileUploadId');
        return;
      }
      isLoadingPreview.value = true;
      previewingFile.value = cert;
      previewFile.value = null;
      previewError.value = null;
      showPreviewModal(fromManage);
      try {
        const result = await fileManagementService.getFileBlob(cert.fileUploadId);
        if (!result?.blob) throw new Error(t('common.files.contentFailed'));
        previewFile.value = buildPreviewFile(result.blob, cert);
      } catch (err) {
        console.error('Error previewing file:', err);
        const msg = t('common.files.previewFailedWithReason', { reason: err.message || t('common.error.unknown') });
        previewError.value = msg;
        store.dispatch('notify', { message: msg, type: 'error' });
      } finally {
        isLoadingPreview.value = false;
      }
    };
    
    // Handle preview success
    const handlePreviewSuccess = () => {
      previewError.value = null;
    };
    
    // Handle preview error
    const handlePreviewError = (error) => {
      console.error('Error in preview:', error);
      previewError.value = t('common.files.loadPreviewFailedWithReason', { reason: error?.message || t('common.error.unknown') });
      
      store.dispatch('notify', {
        message: t('common.files.loadPreviewFailed'),
        type: 'error'
      });
    };
    
    // Download current preview
    const downloadCurrentPreview = () => {
      if (previewingFile.value) {
        downloadFile(previewingFile.value);
      }
    };

    // Load available certificates from databank
    const loadAvailableCertificates = async () => {
      try {
        const response = await certificateService.getCertificates();
        
        if (response && Array.isArray(response)) {
          availableCertificates.value = response;
        } else if (response && response.items) {
          availableCertificates.value = response.items;
        } else {
          availableCertificates.value = [];
        }
      } catch (error) {
        console.error('Error loading available certificates:', error);
        availableCertificates.value = [];
      }
    };

    // Open add certificate modal
    const openAddCertificateModal = (fromManage = false) => {
      resetCertificateForm();
      loadAvailableCertificates();
      if (fromManage && certificatesManageModal) {
        reopenManageAfterAdd.value = true;
        certificatesManageModal.hide();
        setTimeout(() => {
          if (addCertificateModal) {
            addCertificateModal.show();
          }
        }, 220);
        return;
      }
      if (addCertificateModal) {
        addCertificateModal.show();
      }
    };

    const openManageModal = () => {
      if (certificatesManageModal) {
        certificatesManageModal.show();
      }
    };

    // Reset certificate form
    const resetCertificateForm = () => {
      // Set today's date as default for issue date
      const today = new Date().toISOString().split('T')[0];
      
      certificateForm.value = {
        certificateId: '',
        issueDate: today,
        expireDate: '',
        certificateNumber: '',
        selectedFile: null,
        uploadedFileName: ''
      };
      certificateFormErrors.value = {
        certificateId: '',
        issueDate: '',
        expireDate: '',
        file: ''
      };
      selectedCertificateInterval.value = null;
      
      // Reset file input
      if (certificateFileInput.value) {
        certificateFileInput.value.value = '';
      }
      isCertificateFileUploading.value = false;
      certificateFileUploadProgress.value = 0;
    };

    // Handle certificate selection change
    const onCertificateChange = () => {
      const selectedCert = availableCertificates.value.find(
        cert => cert.id === certificateForm.value.certificateId
      );
      
      if (selectedCert && selectedCert.interval) {
        selectedCertificateInterval.value = selectedCert.interval;
        // If issue date is already set, recalculate expire date
        if (certificateForm.value.issueDate) {
          calculateExpireDate();
        }
      } else {
        selectedCertificateInterval.value = null;
        // If no interval, copy issue date to expire date when issue date is set
        if (certificateForm.value.issueDate) {
          certificateForm.value.expireDate = certificateForm.value.issueDate;
        }
      }
    };

    // Handle issue date change
    const onIssueDateChange = () => {
      if (!certificateForm.value.issueDate) return;
      
      if (selectedCertificateInterval.value) {
        calculateExpireDate();
      } else {
        // If no interval, copy issue date to expire date
        certificateForm.value.expireDate = certificateForm.value.issueDate;
      }
    };

    // Handle formatted date input changes
    const onFormattedIssueDateChange = (event) => {
      const value = event.target.value;
      formattedIssueDate.value = value;
      // Trigger change event for date calculation
      if (certificateForm.value.issueDate) {
        onIssueDateChange();
      }
    };

    const onFormattedExpireDateChange = (event) => {
      const value = event.target.value;
      formattedExpireDate.value = value;
    };

    // Calculate expire date based on interval (in years)
    const calculateExpireDate = () => {
      if (!certificateForm.value.issueDate || !selectedCertificateInterval.value) return;
      
      const issueDate = new Date(certificateForm.value.issueDate);
      const expireDate = new Date(issueDate);
      expireDate.setFullYear(expireDate.getFullYear() + selectedCertificateInterval.value);
      
      // Format date as YYYY-MM-DD for input[type="date"]
      certificateForm.value.expireDate = expireDate.toISOString().split('T')[0];
    };

    // Handle certificate file change (validation only, no upload)
    const onCertificateFileChange = (event) => {
      const file = event.target.files[0];
      if (!file) {
        certificateForm.value.selectedFile = null;
        certificateForm.value.uploadedFileName = '';
        return;
      }

      // Clear file validation error immediately when file is selected
      certificateFormErrors.value.file = '';

      // Validate file type
      if (file.type !== 'application/pdf') {
        certificateFormErrors.value.file = t('common.files.onlyPdfAllowed');
        certificateForm.value.selectedFile = null;
        certificateForm.value.uploadedFileName = '';
        return;
      }

      // Validate file size (18MB limit)
      const maxSize = 18 * 1024 * 1024;
      if (file.size > maxSize) {
        certificateFormErrors.value.file = t('profile.certifications.errors.fileTooLarge', { size: formatFileSize(maxSize) });
        certificateForm.value.selectedFile = null;
        certificateForm.value.uploadedFileName = '';
        return;
      }

      // Store the selected file for later upload after certificate creation
      certificateForm.value.selectedFile = file;
      certificateForm.value.uploadedFileName = file.name;
      
      // Clear validation error for valid files
      certificateFormErrors.value.file = '';
    };

    // Validate certificate form
    const validateCertificateForm = () => {
      let isValid = true;
      
      // Clear previous errors
      certificateFormErrors.value = {
        certificateId: '',
        issueDate: '',
        expireDate: '',
        file: ''
      };

      if (!certificateForm.value.certificateId) {
        certificateFormErrors.value.certificateId = t('profile.certifications.validation.certificateTypeRequired');
        isValid = false;
      }

      if (!certificateForm.value.issueDate) {
        certificateFormErrors.value.issueDate = t('profile.certifications.validation.issueDateRequired');
        isValid = false;
      }

      if (!certificateForm.value.expireDate) {
        certificateFormErrors.value.expireDate = t('profile.certifications.validation.expireDateRequired');
        isValid = false;
      }

      // Check if file has been selected
      if (!certificateForm.value.selectedFile) {
        certificateFormErrors.value.file = t('profile.certifications.validation.fileRequired');
        isValid = false;
      }

      // Check if expire date is after issue date
      if (certificateForm.value.issueDate && certificateForm.value.expireDate) {
        const issueDate = new Date(certificateForm.value.issueDate);
        const expireDate = new Date(certificateForm.value.expireDate);
        
        if (expireDate <= issueDate) {
          certificateFormErrors.value.expireDate = t('profile.certifications.validation.expireAfterIssue');
          isValid = false;
        }
      }

      return isValid;
    };

    // Cancel certificate modal
    const cancelCertificateModal = () => {
      // Close modal first
      if (addCertificateModal) {
        addCertificateModal.hide();
      }
      // Reset form will be called by the modal hidden event
    };

    // Helper: Get selected certificate info
    const getSelectedCertificate = () => {
      const selectedCert = availableCertificates.value.find(
        cert => cert.id === certificateForm.value.certificateId
      );
      
      if (!selectedCert) {
        throw new Error(t('profile.certifications.errors.selectedNotFound'));
      }
      
      return selectedCert;
    };

    // Helper: Prepare certificate data for API
    const prepareCertificateData = (selectedCert) => {
      return {
        certificateTypeId: certificateForm.value.certificateId,
        certificateName: certificateForm.value.certificateNumber || selectedCert.name,
        issueDate: certificateForm.value.issueDate,
        expirationDate: certificateForm.value.expireDate
      };
    };

    // Helper: Extract file upload ID from response
    const extractFileUploadId = (uploadResponse) => {
      if (!uploadResponse) return null;
      
      if (uploadResponse.success && uploadResponse.files?.length > 0) {
        return uploadResponse.files[0].id;
      }
      
      if (Array.isArray(uploadResponse) && uploadResponse.length > 0) {
        return uploadResponse[0].id;
      }
      
      if (uploadResponse.id) {
        return uploadResponse.id;
      }
      
      return null;
    };

    // Helper: Upload certificate file
    const uploadCertificateFile = async (certificateId) => {
      isCertificateFileUploading.value = true;
      certificateFileUploadProgress.value = 20;

      const fileDataArray = await fileManagementService.prepareFileUploadDtos(
        [certificateForm.value.selectedFile], 
        certificateId,
        "Certification"
      );

      certificateFileUploadProgress.value = 60;

      const uploadResponse = await fileManagementService.uploadFilesJsonAsync(
        certificateId,
        fileDataArray,
        "Certification"
      );

      certificateFileUploadProgress.value = 100;
      return uploadResponse;
    };

    // Helper: Update certificate with file upload ID
    const updateCertificateWithFile = async (certificateId, fileUploadId, selectedCert) => {
      const updateData = {
        id: certificateId,
        certificateTypeId: certificateForm.value.certificateId,
        certificateName: certificateForm.value.certificateNumber || selectedCert.name,
        issueDate: certificateForm.value.issueDate,
        expirationDate: certificateForm.value.expireDate,
        fileUploadId: fileUploadId
      };

      await tenantCertificateService.updateTenantCertificate(certificateId, updateData);
    };

    // Helper: Handle file upload success
    const handleFileUploadSuccess = async (certificateId, fileUploadId, selectedCert) => {
      await updateCertificateWithFile(certificateId, fileUploadId, selectedCert);
      store.dispatch('notify', {
        message: t('profile.certifications.notifications.addSuccess'),
        type: 'success'
      });
    };

    // Helper: Handle file upload error
    const handleFileUploadError = (error) => {
      console.error('File upload error:', error);
      store.dispatch('notify', {
        message: t('profile.certifications.notifications.fileUploadFailed', { reason: error.message }),
        type: 'warning'
      });
    };

    // Helper: Finalize certificate creation
    const finalizeCertificateCreation = async () => {
      if (addCertificateModal) {
        addCertificateModal.hide();
      }
      resetCertificateForm();
      await loadCertificates();
    };

    // Submit certificate
    const submitCertificate = async () => {
      if (!validateCertificateForm()) return;

      isSubmittingCertificate.value = true;

      try {
        const selectedCert = getSelectedCertificate();
        const certificateData = prepareCertificateData(selectedCert);
        const response = await tenantCertificateService.createTenantCertificate(certificateData);

        if (!response?.id) {
          throw new Error(t('profile.certifications.errors.createFailed'));
        }

        try {
          const uploadResponse = await uploadCertificateFile(response.id);
          const fileUploadId = extractFileUploadId(uploadResponse);

          if (fileUploadId) {
            await handleFileUploadSuccess(response.id, fileUploadId, selectedCert);
          } else {
            console.error('File upload failed - no valid file ID found in response:', uploadResponse);
            store.dispatch('notify', {
              message: t('profile.certifications.notifications.fileIdMissing'),
              type: 'warning'
            });
          }
        } catch (fileUploadError) {
          handleFileUploadError(fileUploadError);
        }

        await finalizeCertificateCreation();
      } catch (error) {
        console.error('Error adding certificate:', error);
        store.dispatch('notify', {
          message: error.message || t('profile.certifications.errors.addFailed'),
          type: 'error'
        });
      } finally {
        isSubmittingCertificate.value = false;
        isCertificateFileUploading.value = false;
        certificateFileUploadProgress.value = 0;
      }
    };
    
    const openFileUpload = () => {
      fileInput.value.click();
    };
    
    const handleFileUpload = async (event) => {
      const files = event.target.files;
      
      if (!files || files.length === 0) return;
      
      try {
        isUploading.value = true;
        uploadProgress.value = 10;
        error.value = null;
        
        // Check for PDF file type
        for (let i = 0; i < files.length; i++) {
          const file = files[i];
          if (file.type !== 'application/pdf') {
            throw new Error(t('common.files.onlyPdfAllowed'));
          }
        }
        
        // Check for file size limits
        const maxSize = 18 * 1024 * 1024; // 18MB
        const totalUploadSize = Array.from(files).reduce((sum, file) => sum + file.size, 0);
        
        if (totalUploadSize > maxSize) {
          throw new Error(t('common.files.totalSizeExceeded', { size: formatFileSize(maxSize) }));
        }
        
        // Check individual file sizes
        for (const file of files) {
          if (file.size > maxSize) {
            throw new Error(t('common.files.fileSizeExceeded', { fileName: file.name, size: formatFileSize(maxSize) }));
          }
        }
        
        uploadProgress.value = 30;
        
        // First create DTOs with base64 data
        const fileDataArray = await fileManagementService.prepareFileUploadDtos(
          Array.from(files), 
          currentUser.value.tenantId,
          "UserProfiles"
        );
        
        uploadProgress.value = 60;
        
        // Use JSON upload method with upload-json endpoint
        await fileManagementService.uploadFilesJsonAsync(
          currentUser.value.tenantId,
          fileDataArray,
          "UserProfiles"
        );
        
        uploadProgress.value = 100;
        
        // Show success notification
        store.dispatch('notify', {
          message: t('profile.certifications.notifications.uploadedCount', { count: files.length }),
          type: 'success'
        });
        
        // Reload certificates
        await loadCertificates();
        
        // Update entity counts
        await updateCertificatesCount();
        
        // Reset file input
        if (fileInput.value) {
          fileInput.value.value = '';
        }
      } catch (err) {
        console.error('Error uploading files:', err);
        error.value = err.message || t('common.files.uploadFailed');
        
        store.dispatch('notify', {
          message: error.value,
          type: 'error'
        });
      } finally {
        // Set a timeout to hide the progress bar
        setTimeout(() => {
          isUploading.value = false;
          uploadProgress.value = 0;
        }, 1000);
      }
    };

    const handleDelete = async (cert) => {
      if (!cert || !cert.id) {
        console.error('Invalid certificate object');
        return;
      }
      
      // Use SweetAlert for confirmation
      Swal.fire({
        title: t('profile.certifications.deleteConfirm.title'),
        text: t('profile.certifications.deleteConfirm.text', { name: cert.name }),
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#dc3545',
        confirmButtonText: t('common.actions.yesDelete'),
        cancelButtonText: t('common.actions.cancel')
      }).then(async (result) => {
        if (result.isConfirmed) {
          try {
            isLoading.value = true;
            deletingId.value = cert.id;
            
            await tenantCertificateService.deleteTenantCertificate(cert.id);
            
            store.dispatch('notify', {
              message: t('profile.certifications.notifications.deleteSuccess'),
              type: 'success'
            });
            
            // Reload certificates
            await loadCertificates();
            
            // Update entity counts
            await updateCertificatesCount();
          } catch (error) {
            console.error('Error deleting certificate:', error);
            store.dispatch('notify', {
              message: t('profile.certifications.notifications.deleteFailed'),
              type: 'error'
            });
          } finally {
            isLoading.value = false;
            deletingId.value = null;
          }
        }
      });
    };

    // Helper function to get correct file name for preview modal
    const getPreviewFileName = (cert) => {
      if (!cert || (!cert.fileUploadName && !cert.name)) return t('common.files.document');
      
      const fileName = cert.fileUploadName || cert.name;
      return fileName.endsWith('.pdf') ? fileName : `${fileName}.pdf`;
    };

    const initPdfPreviewModal = () => {
      const el = document.getElementById('pdfPreviewModal');
      if (!el) return;
      pdfPreviewModal = new window.bootstrap.Modal(el);
      el.addEventListener('hidden.bs.modal', () => {
        previewFile.value = null;
        previewingFile.value = {};
        previewError.value = null;
        if (reopenManageAfterPreview.value && certificatesManageModal) {
          reopenManageAfterPreview.value = false;
          setTimeout(() => certificatesManageModal.show(), 180);
        }
      });
    };

    const initAddCertificateModal = () => {
      const el = document.getElementById('addCertificateModal');
      if (!el) return;
      addCertificateModal = new window.bootstrap.Modal(el);
      el.addEventListener('hidden.bs.modal', () => {
        resetCertificateForm();
        if (reopenManageAfterAdd.value && certificatesManageModal) {
          reopenManageAfterAdd.value = false;
          setTimeout(() => certificatesManageModal.show(), 180);
        }
      });
    };

    const initBootstrapModals = () => {
      if (!window.bootstrap) return;
      initPdfPreviewModal();
      initAddCertificateModal();
      const manageEl = document.getElementById('certificatesManageModal');
      if (manageEl) {
        certificatesManageModal = new window.bootstrap.Modal(manageEl);
      }
    };

    onMounted(() => {
      window.addEventListener(ACCORDION_EVENT, onOtherCardOpened);
      loadCertificates();
      setTimeout(initBootstrapModals, 100);
    });

    onBeforeUnmount(() => {
      window.removeEventListener(ACCORDION_EVENT, onOtherCardOpened);
    });

    return {
      certificates,
      isExpanded,
      isLoading,
      isUploading,
      uploadProgress,
      error,
      fileInput,
      totalSize,
      formatFileSize,
      formatDate,
      downloadFile,
      openFileUpload,
      handleFileUpload,
      handleDelete,
      deletingId,
      handlePreviewFile,
      previewingFile,
      previewFileData: previewFile,
      previewError,
      isLoadingPreview,
      handlePreviewSuccess,
      handlePreviewError,
      downloadCurrentPreview,
      // Certificate modal functions
      openManageModal,
      openAddCertificateModal,
      compactCertificates,
      availableCertificates,
      isSubmittingCertificate,
      selectedCertificateInterval,
      certificateForm,
      certificateFormErrors,
      onCertificateChange,
      onIssueDateChange,
      submitCertificate,
      cancelCertificateModal,
      // File upload functions
      certificateFileInput,
      isCertificateFileUploading,
      certificateFileUploadProgress,
      onCertificateFileChange,
      // Helper functions
      getPreviewFileName,
      // Date formatting
      dateFormat,
      formattedIssueDate,
      formattedExpireDate,
      onFormattedIssueDateChange,
      onFormattedExpireDateChange,
      parseFormattedDate,
      canCreateCertificates,
      canDeleteCertificates,
      toggleCard
    };
  }
};
</script>

<style scoped>
/* Modal-only: the host screen draws the list, so the card contributes no box of its own. */
.cert-card-modal-only {
  display: contents;
}

/* Collapsible VESPROC card head — the profile page stacks these as an accordion. */
.pv-accordion-head {
  cursor: pointer;
  user-select: none;
}

.pv-accordion-head:focus-visible {
  outline: 0;
  box-shadow: inset 0 0 0 2px rgba(var(--vp-primary-rgb), 0.25);
}

.pv-accordion-caret {
  font-size: 0.95rem;
  color: var(--vp-gray-500);
}

.cert-progress {
  height: 0.5rem;
  background: var(--vp-gray-200);
}

.cert-progress .progress-bar {
  background-color: var(--vp-primary);
}

/* ── Compact list (card body) ────────────────────────────────── */
.compact-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.compact-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 9px 12px;
  border: 1px solid var(--vp-gray-300);
  border-radius: var(--vp-radius);
  background: var(--vp-surface);
}

.compact-item-name {
  font-size: 0.8125rem;
  font-weight: 600;
  color: var(--vp-heading-color);
}

.compact-item-dates {
  font-size: 0.72rem;
  color: var(--vp-gray-500);
  font-variant-numeric: tabular-nums;
}

/* ── Add certificate form ────────────────────────────────────── */
.cert-form {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.cert-field {
  display: flex;
  flex-direction: column;
  gap: 5px;
}

/* The native date input sits invisibly over the addon, so clicking the calendar opens the picker. */
.cert-date-group {
  position: relative;
}

.cert-date-picker {
  position: absolute;
  right: 0;
  top: 0;
  bottom: 0;
  width: 40px;
  opacity: 0;
  cursor: pointer;
  z-index: 2;
}

/* ── Manage modal grid ───────────────────────────────────────── */
.cert-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(min(260px, 100%), 1fr));
  gap: 12px;
}

.cert-tile {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 14px;
  border: 1px solid var(--vp-gray-300);
  border-radius: var(--vp-radius);
  background: var(--vp-surface);
}

.cert-tile-head {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 8px;
}

.cert-tile-name {
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--vp-heading-color);
}

.cert-tile-type {
  font-size: 0.72rem;
  color: var(--vp-gray-500);
}

.cert-tile-dates {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.75rem;
  color: var(--vp-gray-600);
  font-variant-numeric: tabular-nums;
}

.cert-tile-view {
  margin-top: auto;
  justify-content: center;
  width: 100%;
}

/* ── Preview ─────────────────────────────────────────────────── */
.cert-preview-body {
  min-height: 70vh;
}

.preview-container {
  height: 70vh;
  overflow-y: auto;
}

/* Fix Excel preview scaling issue when scrolling */
:deep(.excel-preview) {
  height: auto !important;
  min-height: 70vh;
  overflow: visible !important;
  transform: none !important;
  transform-origin: unset !important;
}

:deep(.excel-preview table) {
  transform: none !important;
  min-width: 100%;
}

:deep(.excel-preview .x-spreadsheet) {
  height: 70vh !important;
  overflow: visible;
  transform: none !important;
}

:deep(.excel-preview .x-spreadsheet-sheet) {
  transform: none !important;
  height: auto !important;
}
</style>
